import { Box, GlobalStyles, ThemeProvider } from '@mui/material'
import { enUS, plPL } from '@mui/material/locale'
import { useMemo, useState, type ReactNode } from 'react'
import { useLocale } from '@/contexts/LocaleContext'
import { createAffexyTheme } from '@/theme/affexyTheme'
import ResearcherSidebar, { type SidebarItemId } from './ResearcherSidebar'
import ResearcherTopBar from './ResearcherTopBar'
import TrialExpiredBanner from './TrialExpiredBanner'
import PaymentIssueBanner from './PaymentIssueBanner'
import QuotaBanner from './QuotaBanner'
import EditionPreviewBanner from './EditionPreviewBanner'

type ResearcherShellProps = {
  active?: SidebarItemId
  crumbs: string[]
  onPrimaryAction?: () => void
  /** Hide the top bar's primary "New study" button (e.g. inside the wizard). */
  hideTopBarAction?: boolean
  /** Optional bar rendered between the top bar and the scroll area (e.g. a stepper). */
  subBar?: ReactNode
  /** Optional sticky footer pinned below the scroll area (e.g. wizard nav). */
  footer?: ReactNode
  /** Let the page fill the content area edge to edge and manage its own scrolling (split views). */
  bleed?: boolean
  children: ReactNode
}

const keyframes = (
  <GlobalStyles
    styles={{
      '@keyframes affexy-pulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.35 },
      },
    }}
  />
)

/**
 * Affexy researcher chrome — navy sidebar + light top bar on a light canvas.
 * Scopes the Affexy MUI theme via a nested ThemeProvider so it can be adopted
 * screen-by-screen without changing the app-wide (currently dark) theme.
 */
export default function ResearcherShell({
  active,
  crumbs,
  onPrimaryAction,
  hideTopBarAction,
  subBar,
  footer,
  bleed,
  children,
}: ResearcherShellProps) {
  const { language } = useLocale()
  const theme = useMemo(() => createAffexyTheme(language === 'pl' ? plPL : enUS), [language])
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  return (
    <ThemeProvider theme={theme}>
      {keyframes}
      <Box
        sx={{
          // Pin the shell to the viewport instead of using `height: 100dvh`.
          // On mobile, 100dvh resolves to the *largest* viewport (toolbars
          // retracted), so a 100dvh-tall shell overflows the currently visible
          // area and the window itself scrolls — producing a second scrollbar
          // alongside the inner content scroll. `fixed; inset: 0` takes the
          // shell out of flow so the body can never scroll; the content Box
          // below stays the single scroll container.
          position: 'fixed',
          inset: 0,
          display: 'flex',
          overflow: 'hidden',
          bgcolor: 'background.default',
          color: 'text.primary',
          fontFamily: theme.affexy.fonts.body,
        }}
      >
        <ResearcherSidebar
          active={active}
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
        />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <ResearcherTopBar
            crumbs={crumbs}
            onPrimaryAction={onPrimaryAction}
            hideAction={hideTopBarAction}
            onMenuClick={() => setMobileNavOpen(true)}
          />
          <EditionPreviewBanner />
          <TrialExpiredBanner />
          <PaymentIssueBanner />
          <QuotaBanner />
          {subBar}
          {bleed ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>{children}</Box>
          ) : (
            <Box sx={{ flex: 1, overflow: 'auto', p: { xs: '20px', md: '28px 32px 32px' } }}>{children}</Box>
          )}
          {footer}
        </Box>
      </Box>
    </ThemeProvider>
  )
}
